import React, { useContext } from 'react';
import { User } from '@ordermentum/ui-types';
import { ThemeProvider } from 'emotion-theming';
import { ColorCodes, colorsForTheme } from '../../../foundations/color';
import { genThemedStyled, Theme, ThemeContext } from '../../../utilities/theme';
import S from '../../../foundations/size';
import Logo from '../../../foundations/Logo';
import AccountDropdownTemplate from '../../AccountDropdownTemplate';
import { MEDIA_QUERIES as M } from '../../../foundations/grid';

type Props = {
  params: {
    supplierId: string;
    retailerId?: string;
  };
  sticky?: boolean;
  user: User;
  logout: (...args) => void;
};

type themeStyles = {
  backgroundColor: ColorCodes;
  borderColor: ColorCodes;
  headingColor: ColorCodes;
  textColor: ColorCodes;
  iconBackgroundColor: ColorCodes;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  const colorThemes = {
    backgroundColor: colors.SWAN,
    borderColor: colors.ASH,
    headingColor: colors.SPACE,
    textColor: colors.STORM,
    iconBackgroundColor: colors.SWAN,
  };
  return colorThemes;
};

const ThemeStyled = genThemedStyled<themeStyles>();

const Wrapper = ThemeStyled.div<{ sticky: boolean }>(p => ({
  zIndex: 1,
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  backgroundColor: p.theme.backgroundColor,
  height: '4.5rem',
  borderBottom: `0.063rem solid ${p.theme.borderColor}`,
  padding: `${S.XS} ${S.XXL}`,
  top: 0,
  ...(p?.sticky
    ? { position: 'sticky' }
    : { position: 'fixed', width: '100%' }),
  [M.TABLET_LANDSCAPE_AND_SMALLER]: {
    padding: S.S,
  },
}));

const LogoWrapper = ThemeStyled.div({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
});

const AccountWrapper = ThemeStyled.div({
  marginLeft: S.XXXS,
  width: S.XXL,
  height: S.XXL,
});

const Header = (props: Props) => {
  const { params, user, logout, sticky = false } = props;
  const { email, fullName } = user;
  const theme = getTheme(useContext(ThemeContext));

  const logoLink = email ? '/dashboard' : 'https://app.ordermentum.com/';
  const retailersIds = user?.context?.retailers || [];
  const suppliersIds = user?.context?.suppliers || [];

  return (
    <ThemeProvider theme={theme}>
      <Wrapper sticky={sticky}>
        <LogoWrapper>
          <a href={logoLink}>
            <Logo logo="full" color="orange" size="10.5rem" />
          </a>
        </LogoWrapper>

        {email && (
          <AccountWrapper>
            <AccountDropdownTemplate
              logout={logout}
              params={params}
              user={{
                email,
                fullName,
              }}
              retailersIds={retailersIds}
              suppliersIds={suppliersIds}
              isOnDashboard={false}
            />
          </AccountWrapper>
        )}
      </Wrapper>
    </ThemeProvider>
  );
};

export default Header;
